/**
 * ehr#893: the chart's top tab row shows as many tabs as fit and puts the rest under More, like the
 * Profile tab's section row (ehr#743 V15). No sideways scrollbar at any width.
 */

export type ChartTab = {
  key: string;
  label: string;
  /** The count after the label (Appointments 22, Forms 22, Tasks 1). */
  badge?: number;
  /** The red dot (Follow-up past due). */
  overdue?: boolean;
};

/**
 * How many tabs, from the left, stay on the row. The rest go under More, in the same order.
 *
 * - `tabW[i]`: tab i's width on the row, its right margin included.
 * - `trail`: the last tab's right margin (not needed when every tab fits).
 * - `avail`: the row's inner width.
 * - `active`: the open tab's index (-1 if none).
 * - `moreW`: More's width while it reads "More".
 * - `moreActiveW[i]`: More's width while it is named for tab i (tab i open and under More, the
 *   Profile row's rule: More becomes the open tab).
 */
export function chartTabsFit(o: {
  tabW: readonly number[];
  trail: number;
  avail: number;
  active: number;
  moreW: number;
  moreActiveW: readonly number[];
}): number {
  const n = o.tabW.length;
  const prefix = [0];
  for (const w of o.tabW) prefix.push((prefix[prefix.length - 1] ?? 0) + w);
  // Half a pixel of slack for rounding.
  const fits = (w: number) => w <= o.avail + 0.5;
  const upTo = (k: number) => prefix[k] ?? 0;
  if (n === 0 || fits(upTo(n) - o.trail)) return n;
  for (let k = n - 1; k > 0; k--) {
    const inMore = o.active >= k;
    const more = inMore ? (o.moreActiveW[o.active] ?? o.moreW) : o.moreW;
    if (fits(upTo(k) + more)) return k;
  }
  return 0;
}
